import { Component, Input, OnInit, OnChanges, SimpleChanges, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { ControlConfig, SelectOption } from '../../types/form.types';

/**
 * Form radio button group component for single selection.
 *
 * @remarks Provides a radio button group input with validation support,
 * error messaging, and accessibility features. Integrates with Angular
 * reactive forms and supports dynamic configuration through the ControlConfig
 * interface for options and validation rules.
 */
@Component({
  selector: 'app-form-radio',
  templateUrl: './form-radio.component.html',
  styleUrls: ['./form-radio.component.scss'],
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FormRadioComponent implements OnInit, OnChanges {
  /** @remarks Configuration object defining the radio group behavior and options */
  @Input() controlConfig: ControlConfig;

  /** @remarks Form group that contains this radio group control */
  @Input() formGroup: FormGroup;

  /** @remarks External error state flag for custom validation scenarios */
  @Input() hasError: boolean = false;

  /**
   * Determines if the radio group is in an invalid state.
   *
   * @remarks Checks if the form control is invalid and has been interacted with
   * (touched or dirty) or if an external error flag is set. Used for styling
   * and error message display.
   *
   * @returns True if the radio group should show validation errors
   */
  get isInvalid(): boolean {
    const control = this.formGroup?.get(this.controlConfig.name);
    return (control?.invalid && (control?.touched || control?.dirty)) || this.hasError;
  }

  /**
   * Determines if the radio group is in a valid state.
   *
   * @remarks Checks if the form control is valid and has been interacted with.
   * Used for success styling indicators.
   *
   * @returns True if the radio group should show validation success
   */
  get isValid(): boolean {
    const control = this.formGroup?.get(this.controlConfig.name);
    return control?.valid && (control?.touched || control?.dirty);
  }

  /**
   * Gets the currently selected radio button value.
   *
   * @remarks Returns the value of the selected radio option or undefined
   * if no option is selected. Used for determining which radio button
   * should be marked as checked.
   *
   * @returns The value of the selected radio option
   */
  get selectedValue(): string {
    const control = this.formGroup?.get(this.controlConfig.name);
    return control?.value;
  }

  /**
   * Gets the error message element ID for accessibility.
   *
   * @remarks Generates a unique ID for the error message element to be
   * used in aria-describedby attributes for proper screen reader association.
   *
   * @returns The ID string for the error message element
   */
  get errorId(): string {
    return `error-message-${this.controlConfig.name}`;
  }

  /**
   * Gets the appropriate error message to display.
   *
   * @remarks Determines the correct error message based on validation failures,
   * particularly checking for required field violations when no option is selected.
   *
   * @returns The error message string or null if no errors
   */
  get errorMessage(): string | null {
    if (this.isInvalid) {
      // check if the radio is required and if no value is selected
      if (this.controlConfig.required && !this.formGroup?.get(this.controlConfig.name)?.value) {
        return this.controlConfig.errorMessageRequired || '';
      }
      return this.controlConfig.errorMessage || '';
    }
    return null;
  }

  /**
   * Component initialization lifecycle hook.
   *
   * @remarks Updates the form control's disabled state based on the
   * initial configuration when the component is first initialized.
   */
  ngOnInit(): void {
    this.updateDisabledState();
  }

  /**
   * Handles changes to component inputs.
   *
   * @remarks Updates the form control's disabled state when the control
   * configuration changes, ensuring the UI stays in sync with the config.
   *
   * @param changes - Object containing the changed input properties
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['controlConfig'] && !changes['controlConfig'].firstChange) {
      this.updateDisabledState();
    }
  }

  /**
   * Generates a unique ID for individual radio button elements.
   *
   * @remarks Creates a unique identifier for each radio button option
   * to ensure proper accessibility and form control association.
   *
   * @param index - The index of the radio option in the options array
   * @returns A unique ID string for the radio button element
   */
  getRadioId(index: number): string {
    return `radio-${this.controlConfig.name}-${index}`;
  }

  /**
   * Updates the form control's enabled/disabled state.
   *
   * @remarks Enables or disables the form control based on the configuration
   * settings, ensuring proper form behavior and accessibility.
   */
  private updateDisabledState(): void {
    const control = this.formGroup?.get(this.controlConfig.name);
    if (control) {
      if (this.controlConfig.disabled) {
        control.disable();
      } else {
        control.enable();
      }
    }
  }
}
